import { useEffect, useState } from 'react'
import { Dialog } from 'radix-ui'
import { X } from 'lucide-react'
import { Combobox } from '@/components/common/Combobox'
import { useStatusTracks, useWriteStatusEntry } from '../hooks'
import type { StatusSnapshotEntry } from '../types'

type Props = {
  contractId: number
  /** Snapshot toru, którego dotyczy edycja. Null = dialog zamknięty. */
  entry: StatusSnapshotEntry | null
  onClose: () => void
}

/**
 * Mały dialog do zmiany wartości jednego toru statusu - zamiast zbiorczego sheet
 * dla wszystkich torów. Dropdown wartości (z tracks katalogu) + nota + zapisz.
 * "Wyczyść" ustawia null (przywraca brak statusu).
 */
export function SingleStatusEditDialog({ contractId, entry, onClose }: Props) {
  const tracks = useStatusTracks(true)
  const write = useWriteStatusEntry(contractId)

  const [valueId, setValueId] = useState<number | null>(null)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (entry) {
      setValueId(entry.currentValueId)
      setNote('')
      setError(null)
    }
  }, [entry])

  if (!entry) return null

  const track = tracks.data?.find(t => t.id === entry.trackId)
  const valueChanged = valueId !== entry.currentValueId
  const noteAdded = note.trim().length > 0
  const canSave = (valueChanged || noteAdded) && !saving

  const submit = async () => {
    if (!canSave) return
    setError(null)
    setSaving(true)
    try {
      await write.mutateAsync({
        trackId: entry.trackId,
        valueId,
        note: note.trim() || null,
      })
      onClose()
    } catch (e) {
      setError(formatError(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[440px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl overflow-hidden">
          <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
            <div>
              <Dialog.Title className="text-base font-semibold tracking-tight text-slate-900">
                {entry.trackLabelPl}
              </Dialog.Title>
              <Dialog.Description className="text-xs text-slate-500 mt-0.5 font-mono">
                {entry.trackCode}
              </Dialog.Description>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="size-8 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center"
              aria-label="Zamknij"
            >
              <X size={16} />
            </button>
          </header>

          <div className="px-5 py-4 flex flex-col gap-4 text-sm">
            <label className="grid gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-600">
                Wartość
              </span>
              <Combobox<number>
                value={valueId}
                onChange={v => setValueId(v)}
                options={(track?.values.filter(v => v.isActive) ?? []).map(v => ({ value: v.id, label: v.labelPl }))}
                emptyOptionLabel="— Wyczyść / brak —"
                placeholder="— Wyczyść / brak —"
                disabled={saving || !track}
              />
            </label>

            <label className="grid gap-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-600">
                Nota (opcjonalnie)
              </span>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                disabled={saving}
                rows={3}
                placeholder="Powód zmiany - widoczny w historii zmian"
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-xs focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15 resize-y"
              />
            </label>

            {error && (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {error}
              </div>
            )}
          </div>

          <footer className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50/40">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSave}
              className="rounded-md bg-sky-600 text-white px-3 py-1.5 text-sm font-semibold hover:bg-sky-700 disabled:opacity-50 shadow-sm"
            >
              {saving ? 'Zapisywanie…' : 'Zapisz'}
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd zapisu statusu'
}
